import { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * Public Retention Notice — /legal/retention
 *
 * v1.2026-02
 *
 * Describes ONLY behaviour that is actually implemented in the current
 * ImpactVest codebase. Do not add promises without corresponding shipped
 * controls. Any change here must be paired with a bump of
 * `RETENTION_NOTICE_VERSION` in `/app/backend/live_workspace.py` so newly
 * recorded consents pin to the right version string.
 */
export default function RetentionNotice() {
  useEffect(() => {
    document.title = "Data retention · ImpactVest";
  }, []);

  return (
    <div className="min-h-screen bg-iv-bg text-iv-fg" data-testid="retention-notice-page">
      <header className="border-b border-iv-line bg-iv-surface">
        <div className="iv-container flex items-center justify-between h-[68px]">
          <Link to="/" className="iv-body-sm" style={{ color: "var(--iv-fg-2)", textDecoration: "none" }}>
            ← Back to ImpactVest
          </Link>
          <span className="iv-caption">Legal · Data retention notice · v{VERSION}</span>
        </div>
      </header>

      <main className="iv-container py-14 md:py-20 max-w-[820px] mx-auto">
        <div className="iv-caption mb-4">Legal</div>
        <h1 className="iv-h1 mb-6" data-testid="retention-h1">How ImpactVest stores and processes uploads</h1>
        <p className="iv-body-lg mb-4">
          This notice explains, in plain terms, how documents you upload to a Private Live Workspace are stored,
          processed, retained and removed. It describes the behaviour that is actually implemented today —
          not aspirations for future controls. If the underlying behaviour changes, this notice is updated and
          the version number changes with it.
        </p>
        <p className="iv-caption mb-14">Notice version {VERSION} · Effective {EFFECTIVE_DATE}</p>

        <Section title="What is stored, and where">
          <p className="iv-body mb-4">
            A Private Live Workspace is scoped to your ImpactVest account. Every artefact below is queryable
            only by an authenticated request that both (a) presents a valid ImpactVest session token for the
            owning account and (b) references the workspace by its immutable identifier.
          </p>
          <Category
            head="Source documents"
            summary="The files you upload (PDF, DOCX, XLSX, CSV, text)."
            behaviour="Stored in a MongoDB GridFS collection scoped to your workspace and opportunity. The file's contents are stored as-is; the extraction pipeline (below) reads from this store."
          />
          <Category
            head="Extracted text and citations"
            summary="Parsed text chunks and chunk-header locators used to anchor evidence."
            behaviour="Stored in test_document_extracts. Each chunk references the parent document by its GridFS id, so extracts can only be linked to the original file within the same workspace."
          />
          <Category
            head="Claims and analyses"
            summary="LLM-derived claims classified into the ImpactVest five-value evidence taxonomy, plus your manual annotations, category edits and reviewer assignments."
            behaviour="Stored in test_document_claims and test_claim_revisions (audit-only). Every claim carries an immutable back-reference to at least one document + chunk when it is a source_supported_fact or contradictory_evidence."
          />
          <Category
            head="Draft outputs"
            summary="The working-draft Decision Passport (Investor) or Investor Evidence Pack (Company)."
            behaviour="Composed on-demand from the current claims and opportunity state. There is no separate draft-cache collection to prune — draft output is a live view of your workspace state."
          />
          <Category
            head="Released outputs"
            summary="Immutable snapshots created when you approve a release: a Decision Passport version or an Investor Evidence Pack version."
            behaviour="Stored in test_evidence_pack_versions and the passport_versions surface. Each snapshot captures the byte-identical payload + HMAC-SHA256 evidence signature at the moment of release."
          />
          <Category
            head="Operational and audit records"
            summary="Consent records, workspace-deletion audit records, activity events."
            behaviour="Consent records (live_workspace_consents) and workspace-deletion audit records (live_workspace_deletions) are retained indefinitely as legal evidence that the actions were taken. Deletion audit records contain COUNTS and identifiers only — never document titles, filenames, excerpts or claim text."
          />
          <Category
            head="Backups"
            summary="ImpactVest does not currently operate a workspace-level backup restoration service."
            behaviour="Standard infrastructure snapshots taken by the platform database provider may exist per that provider's policy. Those snapshots are not managed at the workspace level and are not part of the deletion flow described below. If provider-level snapshots exist, they lapse according to the provider's rotation policy."
          />
        </Section>

        <Section title="When retention begins">
          <p className="iv-body mb-3">
            Retention for each artefact begins at the moment it is created:
          </p>
          <ul className="iv-body list-disc pl-6 space-y-2 mb-4">
            <li>Source documents &mdash; when the upload completes successfully.</li>
            <li>Extracts, claims, analyses &mdash; when the analysis pipeline finishes.</li>
            <li>Released outputs &mdash; when you press &ldquo;Release&rdquo; on a version.</li>
            <li>Consent record &mdash; when you press &ldquo;Confirm authorization&rdquo;.</li>
            <li>Deletion audit &mdash; when you press &ldquo;Delete this workspace&rdquo;.</li>
          </ul>
        </Section>

        <Section title="How long each category is retained">
          <p className="iv-body mb-3">
            <b>Customer-controlled retention.</b> There is currently no automated time-based
            deletion of workspace contents. Your source documents, extracts, claims, draft output
            and released versions remain in the workspace until you delete the workspace. Retention
            is bounded by your action — not by a background job — and this is the shipped behaviour.
          </p>
          <p className="iv-body mb-3">
            Consent records and workspace-deletion audit records are retained indefinitely.
            These records contain no document content.
          </p>
          <p className="iv-body">
            The workspace UI shows a retention preference figure (default 90 days) alongside your
            consent record. That figure is currently a stated commitment on your side, not an
            enforced auto-purge. If you require automated time-based deletion, contact
            <a href="mailto:access@impactvest.com" style={{ color: "var(--iv-green)", textDecoration: "underline" }}> access@impactvest.com</a>.
          </p>
        </Section>

        <Section title="What deleting a workspace does">
          <p className="iv-body mb-3">
            Pressing &ldquo;Delete this workspace and all its contents&rdquo; on the workspace page removes,
            in a single server-side operation:
          </p>
          <ul className="iv-body list-disc pl-6 space-y-2 mb-4">
            <li>Every source document uploaded to the workspace (from MongoDB GridFS).</li>
            <li>Every extracted-text chunk in test_document_extracts.</li>
            <li>Every claim, claim revision and reviewer note in test_document_claims and test_claim_revisions.</li>
            <li>Every released passport / evidence-pack version associated with the workspace.</li>
            <li>Every opportunity record in the workspace.</li>
            <li>The workspace document itself.</li>
          </ul>
          <p className="iv-body mb-3">
            A workspace-deletion audit record is written to <code>live_workspace_deletions</code>.
            That audit record contains the workspace id, user id, user email, pathway, deletion
            timestamp and counts of items removed (documents, claims, revisions, extracts, pack
            versions, GridFS blobs). It contains no document titles, no filenames, no excerpts
            and no claim text.
          </p>
          <p className="iv-body mb-3">
            The consent record you signed when the workspace was created is retained as legal
            evidence of the authorisation to upload. It contains your ImpactVest account
            identifier, the signed name, the retention-notice version number you accepted,
            the policy-version identifier and the timestamp. It does not contain any document
            content.
          </p>
          <p className="iv-body">
            Provider-level infrastructure snapshots (if any) are outside the scope of this
            deletion. See the &ldquo;Backups&rdquo; category above.
          </p>
        </Section>

        <Section title="Records that remain after deletion">
          <p className="iv-body mb-3">
            The following records remain after you delete a workspace. They are retained because
            they are legally or operationally necessary as evidence that a consented interaction
            took place:
          </p>
          <ul className="iv-body list-disc pl-6 space-y-2 mb-4">
            <li>
              <b>Consent record</b> — used to demonstrate that authorisation was given at the
              time of upload. Contains: user id, user email, signed name, retention-notice
              version, policy version, timestamp.
            </li>
            <li>
              <b>Deletion audit record</b> — used to demonstrate that deletion occurred and to
              account for the artefacts removed. Contains: user id, user email, workspace id,
              pathway, timestamp and count-only totals.
            </li>
            <li>
              <b>Platform activity events</b> — a limited set of non-content events (register,
              login, checkout, upload-attempt, release, delete). Contains: user id, event name,
              timestamp and a small tag payload. No document content.
            </li>
          </ul>
        </Section>

        <Section title="Provider details">
          <p className="iv-body mb-3">
            ImpactVest is operated by <b>Gaiosis Systems</b>. The customer-facing product identity
            is ImpactVest. Support and privacy contact: <a href="mailto:office@impactvest.com" style={{ color: "var(--iv-green)", textDecoration: "underline" }}>office@impactvest.com</a>.
          </p>
          <p className="iv-body">
            LLM analysis is performed by third-party model providers via the Emergent LLM key
            gateway. Providers process only the text passed to them for the specific request;
            they do not receive your GridFS blobs and they do not persist your uploaded material
            for their own training. See the ImpactVest Privacy Notice for the current provider
            list.
          </p>
        </Section>

        <Section title="Version history">
          <p className="iv-body mb-3">
            <b>v{VERSION}</b> — {EFFECTIVE_DATE}. First public retention notice, published alongside
            Session B (Private Live Workspace pipeline). Describes customer-controlled retention,
            cascade deletion, consent audit and deletion audit as they are actually implemented.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-iv-line-strong">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <Link to="/legal/privacy" className="iv-body-sm hover:underline" style={{ color: "var(--iv-fg-2)" }}>
              Privacy notice →
            </Link>
            <Link to="/legal/terms" className="iv-body-sm hover:underline" style={{ color: "var(--iv-fg-2)" }}>
              Terms of service →
            </Link>
            <Link to="/" className="iv-body-sm hover:underline" style={{ color: "var(--iv-fg-2)" }}>
              Back to ImpactVest →
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

const VERSION = "1.2026-02";
const EFFECTIVE_DATE = "3 February 2026";

function Section({ title, children }) {
  return (
    <section className="mb-14">
      <h2 className="iv-h2 mb-5" style={{ color: "var(--iv-fg)" }}>{title}</h2>
      {children}
    </section>
  );
}

function Category({ head, summary, behaviour }) {
  return (
    <div className="mb-6 border-l-2 border-iv-line-strong pl-5">
      <div className="iv-h4 mb-1" style={{ color: "var(--iv-fg)" }}>{head}</div>
      <div className="iv-body-sm mb-2" style={{ fontStyle: "italic" }}>{summary}</div>
      <div className="iv-body">{behaviour}</div>
    </div>
  );
}
